import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../redux/store';
import axios from 'axios';
import { GET_BASE_URL } from "../constants/apiEndpoints";
import { RootState } from '../redux/store';
import dotsGif from '../assets/img/dots2.gif';
import { fetchMyCollection, fetchStickerLounges, fetchUserMenu } from '../redux/lounges/slice';
import '../css/stickerFairy.css';
import Modal from 'react-modal';
import ReactCrop, { type PixelCrop, type PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { autoTrimTransparentEdges, cropToRect } from '../utils/imageProcessing';
import {
  buildStickerFairyPrompt,
  type StickerFairyPromptMode,
} from '../utils/stickerFairyPrompt';
import { downloadStickerAsset } from '../utils/stickerFairyDownload';
import {
  buildStickerFairyFunctionUrl,
  buildStickerFairyRequestHeaders,
  STICKER_FAIRY_IMAGE_FUNCTION,
  STICKER_FAIRY_TEXT_FUNCTION,
} from '../utils/stickerFairyService';
import {
  movePercentCrop,
  normalizePercentCrop,
  percentCropToPixelCrop,
  resizePercentCrop,
  STICKER_FAIRY_DEFAULT_CROP,
} from '../utils/cropControls';



const STICKER_FAIRY_GENERATION_COST = 20;
const STICKER_FAIRY_CREDIT_CHARGE_CALLS = 2;
const STICKER_FAIRY_COLLECTION_ADD_COST = 10;
const STICKER_FAIRY_HISTORY_LIMIT = 24;

type StickerGenerationResult = {
  original: string;
  transparent: string;
};

type StickerFairySavedGeneration = {
  id: string;
  prompt: string;
  original: string;
  transparent: string;
  createdAt: string;
};

type StickerFairyGenerationFlowOptions = {
  getLatestCreditBalance: () => Promise<number>;
  checkCreditBalance: (balance: number) => void;
  generateSticker: () => Promise<StickerGenerationResult>;
  chargeCredits: (balance: number) => Promise<void>;
  publishSticker: (result: StickerGenerationResult) => Promise<void>;
};

const getCreditBalanceFromCollection = (collection: any) => {
  const item = Array.isArray(collection) ? collection[0] : collection;
  return item?.credit_balance || 0;
};

export const runStickerFairyGenerationFlow = async ({
  getLatestCreditBalance,
  checkCreditBalance,
  generateSticker,
  chargeCredits,
  publishSticker,
}: StickerFairyGenerationFlowOptions) => {
  const latestCreditBalance = await getLatestCreditBalance();
  checkCreditBalance(latestCreditBalance);

  const result = await generateSticker();
  if (!result.original && !result.transparent) {
    throw new Error('Sticker generation failed. Credits were not deducted.');
  }

  await chargeCredits(latestCreditBalance);
  await publishSticker(result);

  return result;
};

export const getCollectionAddButtonLabel = (addCount: number, isAdding: boolean) => {
  if (isAdding) {
    return 'Adding...';
  }

  return addCount > 0
    ? `Add another copy (${STICKER_FAIRY_COLLECTION_ADD_COST} credits)`
    : `Add to Collection (${STICKER_FAIRY_COLLECTION_ADD_COST} credits)`;
};

export const getCollectionAddSuccessMessage = (addCount: number) => {
  if (addCount <= 1) {
    return `Added 1 copy to your collection for ${STICKER_FAIRY_COLLECTION_ADD_COST} credits. Add another copy if you want extras to gift or trade.`;
  }

  return `Added ${addCount} copies to your collection. Each copy costs ${STICKER_FAIRY_COLLECTION_ADD_COST} credits, and you can keep adding more.`;
};

export const normalizeStickerFairyGenerations = (value: unknown): StickerFairySavedGeneration[] => {
  const items = Array.isArray(value)
    ? value
    : Array.isArray((value as { data?: unknown[] })?.data)
      ? (value as { data: unknown[] }).data
      : [];

  if (!items.length) {
    return [];
  }

  return items
    .map((item) => {
      const candidate = item as Record<string, unknown>;
      return {
        id: String(candidate?.id || ''),
        prompt: String(candidate?.prompt || ''),
        original: String(candidate?.original || candidate?.original_image || ''),
        transparent: String(candidate?.transparent || candidate?.transparent_image || ''),
        createdAt: String(candidate?.createdAt || candidate?.createdon || candidate?.created_at || ''),
      };
    })
    .filter((item) => {
      return Boolean(item.id && item.prompt && item.createdAt && (item.original || item.transparent));
    })
    .slice(0, STICKER_FAIRY_HISTORY_LIMIT);
};

export const addStickerFairyHistoryItem = (
  history: StickerFairySavedGeneration[],
  item: StickerFairySavedGeneration
) => {
  const deduped = history.filter((entry) =>
    entry.id !== item.id &&
    !(entry.prompt === item.prompt && entry.original === item.original && entry.transparent === item.transparent)
  );

  return [item, ...deduped].slice(0, STICKER_FAIRY_HISTORY_LIMIT);
};

export const isStickerFairyTransparentCandidate = (originalUrl: string, transparentUrl: string) => {
  if (!transparentUrl) {
    return false;
  }

  if (!originalUrl) {
    return true;
  }

  const normalizeUrl = (url: string) => url.trim().replace(/#.*$/, '');
  return normalizeUrl(originalUrl) !== normalizeUrl(transparentUrl);
};

const imageHasTransparentPixels = (imageUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!imageUrl) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(false);
        return;
      }

      const sampleWidth = Math.min(160, img.naturalWidth || img.width);
      const sampleHeight = Math.min(160, img.naturalHeight || img.height);
      if (!sampleWidth || !sampleHeight) {
        resolve(false);
        return;
      }

      canvas.width = sampleWidth;
      canvas.height = sampleHeight;
      ctx.drawImage(img, 0, 0, sampleWidth, sampleHeight);

      try {
        const pixels = ctx.getImageData(0, 0, sampleWidth, sampleHeight).data;
        for (let index = 3; index < pixels.length; index += 4) {
          if (pixels[index] < 250) {
            resolve(true);
            return;
          }
        }
        resolve(false);
      } catch (error) {
        resolve(true);
      }
    };

    img.onerror = () => resolve(false);
    img.src = imageUrl;
  });
};

const StickerFairy = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [promptMode, setPromptMode] = useState<StickerFairyPromptMode>('sticker');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingToCollection, setIsAddingToCollection] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [productName, setProductName] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [transparentImageUrl, setTransparentImageUrl] = useState('');
  const [hasTransparentSticker, setHasTransparentSticker] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [collectionAddCount, setCollectionAddCount] = useState(0);
  const [savedGenerations, setSavedGenerations] = useState<StickerFairySavedGeneration[]>([]);
  const [deletingGenerationId, setDeletingGenerationId] = useState('');
  const dispatch = useAppDispatch();
  const [addCollectionErrorMessage, setAddCollectionErrorMessage] = useState('');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [baseTransparentUrl, setBaseTransparentUrl] = useState('');
  const [isCropping, setIsCropping] = useState(false);
  const [cropTarget, setCropTarget] = useState<'sticker' | null>(null);
  const [cropSourceUrl, setCropSourceUrl] = useState('');
  const [crop, setCrop] = useState<PercentCrop | undefined>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [cropError, setCropError] = useState('');
  const [cropNotice, setCropNotice] = useState('');
  const cropImageRef = useRef<HTMLImageElement | null>(null);
  const sourceImageInputRef = useRef<HTMLInputElement | null>(null);
  const addToCollectionRef = useRef<HTMLDivElement | null>(null);

  const handleDownload = useCallback(async (fileUrl: string, fileName: string) => {
    await downloadStickerAsset(fileUrl, fileName);
  }, []);

  const clearSourceImage = useCallback(() => {
    setUploadedImage(null);
    if (sourceImageInputRef.current) {
      sourceImageInputRef.current.value = '';
    }
  }, []);

  const scrollToAddToCollection = useCallback(() => {
    window.requestAnimationFrame(() => {
      addToCollectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const refreshStickerPickerData = useCallback(async () => {
    await dispatch(fetchStickerLounges({ emojiData: [] })).unwrap();
  }, [dispatch]);

  const myCollectionItem = useSelector((state: RootState) => {
    const item = state.lounges.myCollectionItem;
    return Array.isArray(item) ? item[0] : item;
  });

  const creditBalance = getCreditBalanceFromCollection(myCollectionItem);

  useEffect(() => {
    Modal.setAppElement('#root');
  }, []);

  const loadSavedGenerations = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(GET_BASE_URL + `/api/v1/getStickerFairyGenerations?limit=${STICKER_FAIRY_HISTORY_LIMIT}`, {
        headers: {
          Authorization: `bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Saved generations could not be loaded.');
      }

      const payload = await response.json();
      setSavedGenerations(normalizeStickerFairyGenerations(payload?.data || payload));
    } catch (error) {
      console.warn('Sticker Fairy saved generations could not be loaded:', error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsCheckingAccess(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/disneyland/login', { state: { returnTo: '/disneyland/StickerFairy', source: 'StickerFairy' } });
        return;
      }

      const club333 = localStorage.getItem('club333');
      const loungeland = localStorage.getItem('loungeland');
      if (club333 === 'true' || loungeland === 'true') {
        setHasAccess(true);
        void dispatch(fetchMyCollection());
        return;
      }

      try {
        // Access flags are set elsewhere (LeftLounge/LandLounge). If a user lands here
        // before that menu fetch completes, localStorage may be empty and they'd be blocked.
        // Refresh rights from the server to avoid false negatives.
        const loginuserid = localStorage.getItem('user_id') || '';
        const menu = await dispatch(fetchUserMenu({ loginuserid })).unwrap();
        const items = Array.isArray(menu) ? menu : [];

        const hasLoungeLand = items.some((item: any) => String(item?.rights_id) === '13');
        const hasClub333 = items.some((item: any) => String(item?.rights_id) === '14');

        localStorage.setItem('loungeland', hasLoungeLand ? 'true' : '');
        localStorage.setItem('club333', hasClub333 ? 'true' : '');

        const allowed = hasLoungeLand || hasClub333;
        setHasAccess(allowed);
        if (allowed) {
          void dispatch(fetchMyCollection());
        }
      } catch (error) {
        console.error('Error checking StickerFairy access:', error);
        setHasAccess(false);
      }
    };
  
    fetchData()
      .catch((error) => console.error('StickerFairy access check failed:', error))
      .finally(() => setIsCheckingAccess(false));
  }, [navigate, dispatch]);

  useEffect(() => {
    if (!hasAccess || isCheckingAccess) {
      return undefined;
    }

    void loadSavedGenerations();

    const refreshDelays = [1500, 5000, 12000];
    const timers = refreshDelays.map((delay) =>
      window.setTimeout(() => {
        void dispatch(fetchMyCollection());
      }, delay)
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [dispatch, hasAccess, isCheckingAccess, loadSavedGenerations]);
    
  const checkCreditBalance = (balance = creditBalance) => {
    if (balance < STICKER_FAIRY_GENERATION_COST) {
      throw new Error(`Insufficient credits. You need at least ${STICKER_FAIRY_GENERATION_COST} credits to generate a sticker.`);
    }
  };

  const updateCreditBalance = async () => {
    try {
      await dispatch(fetchMyCollection());
      // The state will be updated by the Redux action, so no need to set it here
    } catch (error) {
      console.error('Error updating credit balance:', error);
    }
  };
  
  

  const processStickerGeneration = async (
    chargeCalls = STICKER_FAIRY_CREDIT_CHARGE_CALLS,
    balance = creditBalance
  ) => {
    const token = localStorage.getItem('token');

    try {
      checkCreditBalance(balance);

      for (let chargeIndex = 0; chargeIndex < chargeCalls; chargeIndex += 1) {
        const response = await fetch(GET_BASE_URL + "/api/v1/processStickerGen", {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          throw new Error('Credit charge failed. Please try again.');
        }
      }

      await updateCreditBalance(); // Return the updated credit balance
  } catch (error) {
    console.error('Error processing sticker generation:', error);
    if (error instanceof Error) {
      setErrorMessage(error.message);
    } else {
      setErrorMessage('An unexpected error occurred.');
    }
    throw error;
  }
};

  const generateRandomId = () => {
    const randomNumber = Math.floor(Math.random() * 90000) + 10000; // Generate a random number between 10000 and 99999
    return randomNumber.toString(); // Convert the number to a string
  };

  const saveGenerationToHistory = useCallback(async (item: Omit<StickerFairySavedGeneration, 'id' | 'createdAt'>) => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(GET_BASE_URL + '/api/v1/saveStickerFairyGeneration', {
        method: 'POST',
        headers: {
          Authorization: `bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: item.prompt,
          original_image: item.original,
          transparent_image: item.transparent,
        }),
      });

      if (!response.ok) {
        throw new Error('Saved generation could not be created.');
      }

      const payload = await response.json();
      const [savedGeneration] = normalizeStickerFairyGenerations([payload?.data]);
      if (savedGeneration) {
        setSavedGenerations((history) => addStickerFairyHistoryItem(history, savedGeneration));
      } else {
        await loadSavedGenerations();
      }
    } catch (error) {
      console.warn('Sticker Fairy saved generation could not be saved:', error);
    }
  }, [loadSavedGenerations]);

  const deleteSavedGeneration = useCallback(async (generationId: string) => {
    if (!generationId || deletingGenerationId) {
      return;
    }

    const shouldDelete = window.confirm('Delete this saved Sticker Fairy generation?');
    if (!shouldDelete) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    setDeletingGenerationId(generationId);

    try {
      const response = await fetch(GET_BASE_URL + '/api/v1/deleteStickerFairyGeneration', {
        method: 'POST',
        headers: {
          Authorization: `bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          generation_id: generationId,
        }),
      });

      if (!response.ok) {
        throw new Error('Saved generation could not be deleted.');
      }

      setSavedGenerations((history) => history.filter((generation) => generation.id !== generationId));
    } catch (error) {
      console.warn('Sticker Fairy saved generation could not be deleted:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Saved generation could not be deleted.');
    } finally {
      setDeletingGenerationId('');
    }
  }, [deletingGenerationId]);

  const generateThumbnail = (imageUrl: string, setThumbnailUrl: (url: string) => void) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Failed to get canvas context');
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const maxHeight = 150;
      const scale = maxHeight / img.height;
      const targetHeight = maxHeight;
      const targetWidth = Math.max(1, Math.round(img.width * scale));

      canvas.width = targetWidth;
      canvas.height = targetHeight;

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.clearRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      const thumbnail = canvas.toDataURL('image/png');
      setThumbnailUrl(thumbnail);
    };

    img.src = imageUrl;
  };

  const updateCollectionThumbnail = async (imageUrl: string) => {
    const trimmedUrl = await autoTrimTransparentEdges(imageUrl).catch((error) => {
      console.warn('Sticker Fairy thumbnail trim failed:', error);
      return imageUrl;
    });

    generateThumbnail(trimmedUrl, setThumbnailUrl);
  };
  
  const applyPostProcess = async (url: string, originalUrl = originalImageUrl) => {
    let finalUrl = url;
    setCropNotice('');

    try {
      finalUrl = await autoTrimTransparentEdges(url);
    } catch (error) {
      console.warn('Sticker Fairy transparent image trim failed:', error);
    }

    setBaseTransparentUrl(finalUrl);
    setTransparentImageUrl(finalUrl);
    const shouldCheckTransparency = isStickerFairyTransparentCandidate(originalUrl, finalUrl);
    const isTransparent = shouldCheckTransparency
      ? await imageHasTransparentPixels(finalUrl)
      : false;
    setHasTransparentSticker(isTransparent);
    await updateCollectionThumbnail(finalUrl);

    return finalUrl;
  };

  const handleResetCrop = () => {
    if (baseTransparentUrl) {
      setTransparentImageUrl(baseTransparentUrl);
      imageHasTransparentPixels(baseTransparentUrl)
        .then((isTransparent) => setHasTransparentSticker(isTransparent))
        .catch(() => setHasTransparentSticker(false));
      updateCollectionThumbnail(baseTransparentUrl)
        .catch((error) => console.error('Sticker Fairy thumbnail reset failed:', error));
      setCropNotice('');
    }
  };

  const resetCropState = () => {
    setCrop(undefined);
    setCompletedCrop(null);
    setCropError('');
  };

  const closeCropModal = () => {
    setIsCropping(false);
    setCropTarget(null);
    setCropSourceUrl('');
    resetCropState();
  };

  const updateCropSelection = useCallback((nextCrop: PercentCrop) => {
    const normalizedCrop = normalizePercentCrop(nextCrop);
    setCrop(normalizedCrop);
    setCropError('');

    const image = cropImageRef.current;
    if (!image) {
      setCompletedCrop(null);
      return;
    }

    setCompletedCrop(percentCropToPixelCrop(normalizedCrop, image.width, image.height));
  }, []);

  const openCropModal = (target: 'sticker', url: string) => {
    if (!url) {
      return;
    }

    setCropTarget(target);
    setCropSourceUrl(url);
    setIsCropping(true);
    setCrop(STICKER_FAIRY_DEFAULT_CROP);
    setCompletedCrop(null);
    setCropError('');
  };

  const onCropChange = (_pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    updateCropSelection(percentCrop);
  };

  const onCropComplete = (pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
    setCrop(normalizePercentCrop(percentCrop));
    if (pixelCrop.width && pixelCrop.height) {
      setCompletedCrop(pixelCrop);
    } else {
      setCompletedCrop(null);
    }
  };

  const onCropImageLoad = () => {
    updateCropSelection(crop || STICKER_FAIRY_DEFAULT_CROP);
  };

  const handleCropMove = (deltaX: number, deltaY: number) => {
    updateCropSelection(movePercentCrop(crop, deltaX, deltaY));
  };

  const handleCropResize = (delta: number) => {
    updateCropSelection(resizePercentCrop(crop, delta));
  };

  const handleCropResetSelection = () => {
    updateCropSelection(STICKER_FAIRY_DEFAULT_CROP);
  };

  const handleCropApply = async () => {
    if (!cropTarget) {
      return;
    }

    const activeCrop = completedCrop || (
      crop && cropImageRef.current
        ? percentCropToPixelCrop(crop, cropImageRef.current.width, cropImageRef.current.height)
        : null
    );

    if (!activeCrop || activeCrop.width <= 0 || activeCrop.height <= 0) {
      setCropError('Select an area to crop.');
      return;
    }

    try {
      const newUrl = await cropToRect(cropSourceUrl, activeCrop, crop);

      if (cropTarget === 'sticker') {
        setTransparentImageUrl(newUrl);
        const isTransparent = await imageHasTransparentPixels(newUrl);
        setHasTransparentSticker(isTransparent);
        await updateCollectionThumbnail(newUrl);
        setCropNotice('');
      }

      closeCropModal();
    } catch (error) {
      console.error('Manual crop failed:', error);
      setCropError('Cropping unavailable for this image.');

      if (cropTarget === 'sticker') {
        setCropNotice('Cropping unavailable for this image.');
      }
    }
  };

  useEffect(() => {
    const reapply = async () => {
      if (!baseTransparentUrl) {
        return;
      }

      if (baseTransparentUrl) {
        setTransparentImageUrl(baseTransparentUrl);
        const isTransparent = await imageHasTransparentPixels(baseTransparentUrl);
        setHasTransparentSticker(isTransparent);
        await updateCollectionThumbnail(baseTransparentUrl);
        setCropNotice('');
      }
    };

    reapply().catch((error) => console.error('Image reapply failed:', error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTransparentUrl]);


  if (isCheckingAccess) {
    return (
      <div className="sticker-fairy-page">
        <div className="loading-container">
          <img src={dotsGif} alt="Loading" />
          <span>Checking access...</span>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div>
        <h2>StickerFairy is currently for Club 333 and Loungeland members only</h2>
        <p>We are working to make it available to all users soon!</p>
      </div>
    );
  }

  

  const addStickerFairyToCollection = async () => {
    try {
      setIsAddingToCollection(true);
      setAddCollectionErrorMessage('');
      setSuccessMessage('');

      if (!thumbnailUrl) {
        throw new Error('Generate a collection sticker first.');
      }

      if (!productName.trim()) {
        throw new Error('Name your sticker before adding it.');
      }

      const collection = await dispatch(fetchMyCollection()).unwrap();
      const latestCreditBalance = getCreditBalanceFromCollection(collection);

      if (latestCreditBalance < STICKER_FAIRY_COLLECTION_ADD_COST) {
        throw new Error(`Insufficient credits. You need at least ${STICKER_FAIRY_COLLECTION_ADD_COST} credits to add another copy to your collection.`);
      }

      const token = localStorage.getItem('token');
      const productId = generateRandomId();
      const response = await axios.post(
        GET_BASE_URL + "/api/v1/addStickerFairytoCollection",
        {
          product_id: productId, 
          product_name: productName.trim(),
          product_image: thumbnailUrl, // Thumbnail image URL
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (response.status === 200) {
        console.log('Sticker Fairy added to collection successfully');
        const nextAddCount = collectionAddCount + 1;
        setCollectionAddCount(nextAddCount);
        setSuccessMessage(getCollectionAddSuccessMessage(nextAddCount));
        Promise.all([
          dispatch(fetchMyCollection()).unwrap(),
          refreshStickerPickerData(),
        ]).catch((refreshError) => {
          console.error('Failed to refresh collection or sticker picker after Sticker Fairy add:', refreshError);
        });
      } else {
        throw new Error('Failed to add Sticker Fairy to collection');
      }
    } catch (error) {
      console.error('Error adding Sticker Fairy to collection:', error);
      if (axios.isAxiosError(error)) {
        const errData = error.response?.data as any;
        const message =
          (typeof errData === 'string' && errData) ||
          errData?.message ||
          errData?.error ||
          error.message;
        setAddCollectionErrorMessage(message);
      } else if (error instanceof Error) {
        setAddCollectionErrorMessage(error.message);
      } else {
        setAddCollectionErrorMessage('An unexpected error occurred.');
      }
    } finally {
      setIsAddingToCollection(false);
    }
  };
  
  
  

  const generateImage = async (
    prompt: string,
    mode: StickerFairyPromptMode
  ): Promise<StickerGenerationResult> => {
    setErrorMessage('');
    try {
      const modifiedPrompt = buildStickerFairyPrompt(prompt, mode);
      const response = await axios.post(
        buildStickerFairyFunctionUrl(STICKER_FAIRY_TEXT_FUNCTION),
        {
          prompt: modifiedPrompt,
        },
        {
          headers: buildStickerFairyRequestHeaders({ contentType: 'application/json' }),
        }
      );
      const prediction = response.data;
      console.log('Prediction:', prediction);

      // The Supabase Edge Function can return HTTP 200 with a body
      // that has no image output (e.g. Gemini refused the prompt or
      // returned no candidates). Surface whatever message is in the
      // response so the user knows WHY no sticker came back instead
      // of a vague "No image generated."
      if (!prediction || !Array.isArray(prediction.output) || !prediction.output[0]) {
        const serverDetail =
          (typeof prediction === 'string' && prediction) ||
          prediction?.error ||
          prediction?.message ||
          prediction?.detail ||
          prediction?.geminiError ||
          (typeof prediction?.geminiResponse === 'string'
            ? prediction.geminiResponse
            : prediction?.geminiResponse?.error?.message) ||
          (prediction
            ? `Unexpected response from sticker service: ${JSON.stringify(prediction).slice(0, 300)}`
            : 'No response from sticker service.');
        console.error('Gemini text-to-sticker failed:', prediction);
        setErrorMessage(String(serverDetail));
        setBaseTransparentUrl('');
        setHasTransparentSticker(false);
        setCropNotice('');
        return { original: '', transparent: '' };
      }

      // Extract the image URLs from the prediction object. The second output is
      // usually the transparent sticker; fall back to the full-size artwork if
      // the service only returns one asset.
      const originalImageUrl = prediction.output[0];
      const transparentImageUrl = prediction.output[1] || originalImageUrl;

      console.log('Original Image URL:', originalImageUrl);
      console.log('Transparent Image URL:', transparentImageUrl);

      return { original: originalImageUrl, transparent: transparentImageUrl };
    } catch (error) {
      console.error('Error generating image:', error);
      if (axios.isAxiosError(error)) {
        const errData = error.response?.data as any;
        // Surface the most specific message available — including
        // nested Gemini error structures.
        const serverMessage =
          (typeof errData === 'string' && errData) ||
          errData?.error ||
          errData?.message ||
          errData?.detail ||
          errData?.geminiError ||
          (typeof errData?.geminiResponse === 'string'
            ? errData.geminiResponse
            : errData?.geminiResponse?.error?.message) ||
          error.message;
        const status = error.response?.status;
        const prefix = status ? `(HTTP ${status}) ` : '';
        setErrorMessage(
          serverMessage
            ? `${prefix}${serverMessage}`
            : `${prefix}An unexpected error occurred.`
        );
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
      setBaseTransparentUrl('');
      setHasTransparentSticker(false);
      setCropNotice('');
      return { original: '', transparent: '' };
    }
  };
  
  
  const generateImageToSticker = async (
    uploadedImage: File | null,
    prompt: string,
    mode: StickerFairyPromptMode
  ): Promise<StickerGenerationResult> => {
    setErrorMessage('');

    try {
      if (!uploadedImage) {
        setErrorMessage('No image file selected.');
        return { original: '', transparent: '' };
      }

      const allowedFileTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!allowedFileTypes.includes(uploadedImage.type)) {
        setErrorMessage('Unsupported image file type.');
        return { original: '', transparent: '' };
      }

      const modifiedPrompt = buildStickerFairyPrompt(prompt, mode);

      const formData = new FormData();
      formData.append('uploadedImage', uploadedImage, uploadedImage.name);
      formData.append('prompt', modifiedPrompt);

      const response = await axios.post(
        buildStickerFairyFunctionUrl(STICKER_FAIRY_IMAGE_FUNCTION),
        formData,
        {
          headers: buildStickerFairyRequestHeaders(),
          timeout: 150000,
        }
      );

      const prediction = response.data;
      console.log('Prediction:', prediction);

      // The Supabase Edge Function CAN return HTTP 200 with a body
      // that doesn't include image output (e.g. when Gemini refuses
      // the prompt, or when the function ran but produced no
      // candidates). Surface whatever message is in the response so
      // the user knows WHY no sticker came back instead of a vague
      // "No image generated."
      if (!prediction || !Array.isArray(prediction.output) || !prediction.output[0]) {
        const serverDetail =
          (typeof prediction === 'string' && prediction) ||
          prediction?.error ||
          prediction?.message ||
          prediction?.detail ||
          prediction?.geminiError ||
          (typeof prediction?.geminiResponse === 'string'
            ? prediction.geminiResponse
            : prediction?.geminiResponse?.error?.message) ||
          (prediction
            ? `Unexpected response from sticker service: ${JSON.stringify(prediction).slice(0, 300)}`
            : 'No response from sticker service.');
        console.error('Gemini sticker generation failed:', prediction);
        setErrorMessage(String(serverDetail));
        setBaseTransparentUrl('');
        setHasTransparentSticker(false);
        setCropNotice('');
        return { original: '', transparent: '' };
      }

      // Extract the image URLs from the prediction object. The second output is
      // usually the transparent sticker; fall back to the full-size artwork if
      // the service only returns one asset.
      const originalImageUrl = prediction.output[0];
      const transparentImageUrl = prediction.output[1] || originalImageUrl;

      console.log('Original Image URL:', originalImageUrl);
      console.log('Transparent Image URL:', transparentImageUrl);

      return { original: originalImageUrl, transparent: transparentImageUrl };
    } catch (error) {
      console.error('Error generating image to sticker:', error);
      if (axios.isAxiosError(error)) {
        const errData = error.response?.data as any;
        // Surface the most specific message the server provided —
        // including nested Gemini error structures.
        const serverMessage =
          (typeof errData === 'string' && errData) ||
          errData?.error ||
          errData?.message ||
          errData?.detail ||
          errData?.geminiError ||
          (typeof errData?.geminiResponse === 'string'
            ? errData.geminiResponse
            : errData?.geminiResponse?.error?.message) ||
          error.message;
        const status = error.response?.status;
        const prefix = status ? `(HTTP ${status}) ` : '';
        setErrorMessage(
          serverMessage
            ? `${prefix}${serverMessage}`
            : `${prefix}An unexpected error occurred.`
        );
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
      setBaseTransparentUrl('');
      setHasTransparentSticker(false);
      setCropNotice('');
      return { original: '', transparent: '' };
    }
  };


  const handleGenerateSticker = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) {
      setErrorMessage('Describe the sticker you want to make.');
      return;
    }

    try {
      setAddCollectionErrorMessage('');
      setSuccessMessage('');
      setCollectionAddCount(0);
      setErrorMessage('');
      setTransparentImageUrl('');
      setOriginalImageUrl('');
      setThumbnailUrl('');
      setBaseTransparentUrl('');
      setHasTransparentSticker(false);
      setCropNotice('');
      setIsLoading(true);

      await runStickerFairyGenerationFlow({
        getLatestCreditBalance: async () => {
          const collection = await dispatch(fetchMyCollection()).unwrap();
          return getCreditBalanceFromCollection(collection);
        },
        checkCreditBalance,
        generateSticker: () => uploadedImage
          ? generateImageToSticker(uploadedImage, trimmedPrompt, promptMode)
          : generateImage(trimmedPrompt, promptMode),
        chargeCredits: (latestCreditBalance) =>
          processStickerGeneration(STICKER_FAIRY_CREDIT_CHARGE_CALLS, latestCreditBalance),
        publishSticker: async (result) => {
          const stickerUrl = result.transparent || result.original;
          const originalUrl = result.original || stickerUrl;
          setOriginalImageUrl(result.original || stickerUrl);
          const transparentUrl = await applyPostProcess(stickerUrl, originalUrl);
          await saveGenerationToHistory({
            prompt: trimmedPrompt,
            original: originalUrl,
            transparent: transparentUrl,
          });
          setPrompt('');
          clearSourceImage();
        },
      });
    } catch (error) {
      console.error('Failed to process sticker generation or generate image:', error);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };


  const renderImageCard = (
    title: string,
    url: string,
    downloadName: string,
    variant: 'default' | 'thumbnail' | 'fullsize' = 'default',
    actions?: React.ReactNode
  ) => {
    if (!url) {
      return null;
    }

    const cardClassName = ['generated-card'];
    if (variant === 'thumbnail') {
      cardClassName.push('generated-card--thumbnail');
    }
    if (variant === 'fullsize') {
      cardClassName.push('generated-card--fullsize');
    }

    return (
      <div className={cardClassName.join(' ')}>
        <h4>{title}</h4>
        <a
          href={url}
          download={downloadName}
          className="generated-card__download"
          onClick={(event) => {
            event.preventDefault();
            handleDownload(url, downloadName);
          }}
        >
          <img src={url} alt={title} />
          <span className="generated-card__hint">Tap to download</span>
        </a>
        {actions && <div className="generated-card__actions">{actions}</div>}
      </div>
    );
  };

  const restoreSavedGeneration = async (generation: StickerFairySavedGeneration) => {
    const stickerUrl = generation.transparent || generation.original;
    setPrompt(generation.prompt);
    clearSourceImage();
    setOriginalImageUrl(generation.original || stickerUrl);
    setHasTransparentSticker(false);
    setCollectionAddCount(0);
    setSuccessMessage('');
    setAddCollectionErrorMessage('');
    setErrorMessage('');
    await applyPostProcess(stickerUrl, generation.original || stickerUrl);
    scrollToAddToCollection();
  };


return (
  <div className="sticker-fairy-page">
    <div className="sticker-fairy-topbar">
      <div className="sticker-fairy-hero">
        <TransformWrapper>
          <TransformComponent
            wrapperClass="sticker-fairy-workflow-zoom"
            contentClass="sticker-fairy-workflow-zoom__content"
          >
            <img
              className="sticker-fairy-workflow-banner"
              src="/static/images/sticker-fairy/workflow-banner.jpeg"
              alt="How to use Sticker Fairy"
            />
          </TransformComponent>
        </TransformWrapper>
      </div>

      <div className="sticker-fairy-credit-balance" aria-label="Available Sticker Fairy credits">
        <span>Available credits</span>
        <strong>{creditBalance}</strong>
      </div>
    </div>

    <section className="generator-section">
      <div className="generator-body">
        <div className="generator-input">
          <div className="generator-heading">
            <h3 className="generator-title">StickerFairy 3.0</h3>
            <span>{STICKER_FAIRY_GENERATION_COST} credits per generation</span>
          </div>

          <div className="sticker-fairy-mode-toggle" role="group" aria-label="Sticker Fairy mode">
            <button
              type="button"
              className={promptMode === 'sticker' ? 'active' : ''}
              aria-pressed={promptMode === 'sticker'}
              onClick={() => setPromptMode('sticker')}
            >
              Sticker
            </button>
            <button
              type="button"
              className={promptMode === 'art' ? 'active' : ''}
              aria-pressed={promptMode === 'art'}
              onClick={() => setPromptMode('art')}
            >
              Art
            </button>
          </div>

          <textarea
            id="stickerPrompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={
              promptMode === 'sticker'
                ? 'Describe your sticker, e.g. magical text sticker that says "HALLOWEENTIME at DISNEYLAND"'
                : 'Describe your art, e.g. a cozy watercolor castle at sunrise'
            }
            rows={3}
          />

          <div className="file-input">
            <label htmlFor="stickerSourceImage">Add image</label>
            <input
              id="stickerSourceImage"
              ref={sourceImageInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setUploadedImage(e.target.files[0]);
                }
              }}
            />
            {uploadedImage && (
              <div className="file-name">
                <span>{uploadedImage.name}</span>
                <button type="button" onClick={clearSourceImage}>
                  Remove
                </button>
              </div>
            )}
          </div>

          <button onClick={handleGenerateSticker} disabled={isLoading}>
            Generate {promptMode === 'sticker' ? 'Sticker' : 'Art'} ({STICKER_FAIRY_GENERATION_COST} Credits)
          </button>

          {errorMessage && <p className="feedback feedback--error">{errorMessage}</p>}

          {isLoading && (
            <div className="loading-container">
              <img src={dotsGif} alt="Loading" />
              <span>Generating your sticker...</span>
            </div>
          )}
        </div>

        {transparentImageUrl && !isLoading && (
          <div className="generator-output">
            <>
              <div ref={addToCollectionRef} className="generated-card generated-card--action">
                <h4>Next step</h4>
                {thumbnailUrl && (
                  <div className="generated-card__preview" aria-label="MouseWait Sticker preview">
                    <img src={thumbnailUrl} alt="MouseWait Sticker preview" />
                    <span>MouseWait Sticker</span>
                    {!hasTransparentSticker && transparentImageUrl && (
                      <div className="crop-actions">
                        <button type="button" onClick={() => openCropModal('sticker', transparentImageUrl)}>
                          Edit Crop
                        </button>
                        {baseTransparentUrl && (
                          <button type="button" onClick={handleResetCrop}>
                            Reset Crop
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <p className="generated-card__note">
                  Each add creates one collection copy for {STICKER_FAIRY_COLLECTION_ADD_COST} credits. Add as many copies as you want for gifting or trading.
                </p>
                <p className="generated-card__crop-reminder">
                  For best Lounge usability, use Edit Crop before adding it to your collection.
                </p>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Name your sticker"
                />
                <button
                  disabled={!productName.trim() || !thumbnailUrl || isAddingToCollection}
                  onClick={() => {
                    addStickerFairyToCollection().catch((error) => {
                      console.error('Error adding Sticker Fairy to collection:', error);
                      if (error instanceof Error) {
                        setAddCollectionErrorMessage(error.message);
                      } else {
                        setAddCollectionErrorMessage('An unexpected error occurred.');
                      }
                    });
                  }}
                >
                  {getCollectionAddButtonLabel(collectionAddCount, isAddingToCollection)}
                </button>
                <button
                  type="button"
                  className="generated-card__secondary"
                  onClick={() => {
                    setTransparentImageUrl('');
                    setOriginalImageUrl('');
                    setThumbnailUrl('');
                    setBaseTransparentUrl('');
                    setHasTransparentSticker(false);
                    setProductName('');
                    setSuccessMessage('');
                    setCollectionAddCount(0);
                    setAddCollectionErrorMessage('');
                    setCropNotice('');
                    setPrompt('');
                    clearSourceImage();
                  }}
                >
                  Make Another
                </button>
                {addCollectionErrorMessage && <p className="feedback feedback--error">{addCollectionErrorMessage}</p>}
                {successMessage && (
                  <p className="feedback feedback--success">
                    {successMessage}{' '}
                    <Link to="/disneyland/mycollection">View your collection</Link>
                  </p>
                )}
              </div>

              {hasTransparentSticker && renderImageCard(
                'Transparent sticker',
                transparentImageUrl,
                'transparent_sticker.png',
                'default',
                transparentImageUrl && (
                  <>
                    <div className="crop-actions">
                      <button type="button" onClick={() => openCropModal('sticker', transparentImageUrl)}>
                        Edit Crop
                      </button>
                      {baseTransparentUrl && (
                        <button type="button" onClick={handleResetCrop}>
                          Reset Crop
                        </button>
                      )}
                    </div>
                    <p className="generated-card__crop-reminder">
                      Crop this transparent sticker so it fills the Lounge sticker area before adding it.
                    </p>
                    {cropNotice && <p className="feedback feedback--error">{cropNotice}</p>}
                  </>
                )
              )}

              {renderImageCard('Full-size artwork', originalImageUrl, 'full_size_sticker.png', 'fullsize')}
              {renderImageCard('MouseWait Sticker', thumbnailUrl, 'mousewait_sticker.png', 'thumbnail')}
            </>
          </div>
        )}
      </div>
    </section>

    {savedGenerations.length > 0 && (
      <section className="saved-generations" aria-labelledby="saved-generations-title">
        <div className="saved-generations__heading">
          <h3 id="saved-generations-title">Saved Generations</h3>
          <span>{savedGenerations.length} saved to your account</span>
        </div>
        <div className="saved-generations__grid">
          {savedGenerations.map((generation) => {
            const previewUrl = generation.transparent || generation.original;
            return (
              <article className="saved-generation-card" key={generation.id}>
                <button
                  type="button"
                  className="saved-generation-card__preview"
                  onClick={() => restoreSavedGeneration(generation)}
                  aria-label="Load saved Sticker Fairy generation"
                >
                  <img src={previewUrl} alt="Saved Sticker Fairy generation" />
                </button>
                <p className="saved-generation-card__prompt">{generation.prompt}</p>
                <time dateTime={generation.createdAt}>
                  {new Date(generation.createdAt).toLocaleString()}
                </time>
                <div className="saved-generation-card__actions">
                  <button
                    type="button"
                    onClick={() => {
                      setPrompt(generation.prompt);
                      clearSourceImage();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Reuse Prompt
                  </button>
                  <button
                    type="button"
                    onClick={() => restoreSavedGeneration(generation)}
                  >
                    Load Image
                  </button>
                  {generation.original && (
                    <button
                      type="button"
                      onClick={() => handleDownload(generation.original, 'saved_full_size_sticker.png')}
                    >
                      Full-res
                    </button>
                  )}
                  {generation.transparent && (
                    <button
                      type="button"
                      onClick={() => handleDownload(generation.transparent, 'saved_transparent_sticker.png')}
                    >
                      Transparent
                    </button>
                  )}
                  <button
                    type="button"
                    className="saved-generation-card__delete"
                    disabled={deletingGenerationId === generation.id}
                    onClick={() => deleteSavedGeneration(generation.id)}
                  >
                    {deletingGenerationId === generation.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    )}

    <Modal
      isOpen={isCropping}
      onRequestClose={closeCropModal}
      contentLabel="Edit Crop"
      className="sticker-fairy-crop-modal"
      overlayClassName="sticker-fairy-crop-modal__overlay"
    >
      <div className="crop-modal-content">
        <h3>Edit Crop</h3>
        {cropSourceUrl && (
          <div className="crop-modal-canvas">
            <div className="crop-modal-cropper">
              <ReactCrop
                crop={crop}
                onChange={onCropChange}
                onComplete={onCropComplete}
                keepSelection
              >
                <img
                  ref={cropImageRef}
                  src={cropSourceUrl}
                  alt="Crop preview"
                  onLoad={onCropImageLoad}
                />
              </ReactCrop>
            </div>
          </div>
        )}
        {cropError && <p className="crop-modal-error">{cropError}</p>}
        <div className="crop-modal-controls" aria-label="Crop adjustment controls">
          <div className="crop-modal-control-group crop-modal-control-group--move" aria-label="Move crop area">
            <button type="button" onClick={() => handleCropMove(0, -4)} aria-label="Move crop up">
              ↑
            </button>
            <button type="button" onClick={() => handleCropMove(-4, 0)} aria-label="Move crop left">
              ←
            </button>
            <button type="button" onClick={() => handleCropMove(4, 0)} aria-label="Move crop right">
              →
            </button>
            <button type="button" onClick={() => handleCropMove(0, 4)} aria-label="Move crop down">
              ↓
            </button>
          </div>
          <div className="crop-modal-control-group" aria-label="Resize crop area">
            <button type="button" onClick={() => handleCropResize(-6)}>
              Smaller
            </button>
            <button type="button" onClick={() => handleCropResize(6)}>
              Larger
            </button>
            <button type="button" onClick={handleCropResetSelection}>
              Reset
            </button>
          </div>
        </div>
        <div className="crop-modal-actions">
          <button type="button" onClick={handleCropApply}>
            Apply
          </button>
          <button type="button" onClick={closeCropModal}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  </div>
);
}

export default StickerFairy;
